import { EventEmitter, inject, Injectable, Injector } from '@angular/core';
import { DefaultLangChangeEvent, LangChangeEvent, TranslateService, TranslationChangeEvent } from '@ngx-translate/core';
import { fromEventPattern, merge, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { LANGUAGE } from 'shared-components';

import { TranslationLoaderHelper } from 'app/core/internationalization/translation-loader-helper';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  public static readonly SESSION_LANGUAGE_KEY = 'KH.Locale';
  public static readonly DEFAULT_LANGUAGE = LANGUAGE.ENGLISH_CANADA;

  private readonly injector = inject(Injector);

  private _translateService!: TranslateService;

  /**
   * Listens to the translate service for all updates on the keys and emits when there is any change
   * This mimics the exact functionality of the (| translate) pipe and allows us to us it in the typescript
   */
  public translationsReady$ = new Observable<TranslateService>();

  public initializeTranslations(): void {
    this._translateService = this.injector.get(TranslateService);
    const defaultLang = TranslationService.DEFAULT_LANGUAGE;

    this._translateService.setDefaultLang(defaultLang);
    this._translateService.use(defaultLang);

    this.translationsReady$ = this.createReadyStream(this._translateService);
  }

  /**
   * Loads the given language stored in session.
   * @returns the language that was loaded
   */
  public loadLanguage(locale: string = LANGUAGE.ENGLISH_CANADA): void {
    if (locale !== this.getCurrentLanguage()) {
      this.switchLanguage(locale);
    }
  }

  public switchLanguage(languageValue: string): void {
    let language = TranslationService.DEFAULT_LANGUAGE;

    if (Object.values(LANGUAGE).includes(languageValue as LANGUAGE)) {
      language = languageValue as LANGUAGE;
    }

    this._translateService.use(language);
    sessionStorage.setItem(TranslationService.SESSION_LANGUAGE_KEY, JSON.stringify({ LocaleId: language }));
  }

  private createReadyStream(translateService: TranslateService) {
    const langChange$ = this.fromEmitter<LangChangeEvent>(translateService.onLangChange);
    const defaultLangChange$ = this.fromEmitter<DefaultLangChangeEvent>(
      translateService.onDefaultLangChange
    );
    const translationChange$ = this.fromEmitter<TranslationChangeEvent>(
      translateService.onTranslationChange
    );

    const loading$ = this.injector.get(TranslationLoaderHelper).loading$;

    return merge(langChange$, defaultLangChange$, translationChange$).pipe(
      startWith(undefined),
      switchMap(() =>
        loading$.pipe(
          filter((isLoading) => !isLoading), // only emit when loading finishes to ensure translations are ready
          take(1)
        )
      ),
      map(() => translateService),
      shareReplay({ bufferSize: 1, refCount: true }) // send the last emmission to new subscribers
    );
  }

  /**
   * Converts EventEmitters into Observables for easy chaining
   */
  public fromEmitter<T>(emitter: EventEmitter<T>) {
    return fromEventPattern<T>(
      (handler) => emitter.subscribe(handler),
      (_handler, subscription) => subscription.unsubscribe()
    );
  }

  /**
   * Wrapper around TranslateService.currentLang
   * @returns
   */
  public getCurrentLanguage(): LANGUAGE {
    return this._translateService.currentLang as LANGUAGE;
  }

  /**
   * Wrapper around TranslateService.instant
   */
  public instant(key: string, params?: object): string {
    return this._translateService.instant(key, params);
  }

  /**
   * Wrapper around TranslateService.get
   */
  public get(key: string | string[], params?: object): Observable<string> {
    return this._translateService.get(key, params);
  }
}
